import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { Button as ButtonPrimitive } from "@base-ui/react/button"

// The intro wave should play once per browser session, not on every auth route
// (sign-in → consent → accept-invitation each mount a fresh AuthShell, and the
// flow can do full-page redirects). We remember it in sessionStorage so the hand
// just rests after the first play; the right-edge hover replay stays unaffected.
const WAVE_SEEN_KEY = "attend.authWaveSeen"
// useLayoutEffect would warn during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect

// Auth frame — admin's dark eggplant radial background + the cute 👋 wave
// flourish (rises + waves once per session, replays when the pointer enters the
// card's right-edge hotspot), with the card + controls rendered via Base UI.
// Always dark so the card, controls, wordmark, and footer stay legible together.
export function AuthShell({
  title,
  subtitle,
  wave = false,
  children,
}: {
  title: string
  subtitle?: string
  wave?: boolean
  children: ReactNode
}) {
  const [hasIntroPlayed, setHasIntroPlayed] = useState(false)
  const [isReplaying, setIsReplaying] = useState(false)

  // Seed from sessionStorage before paint so an already-seen session skips the
  // intro without flashing (initial render stays false to match SSR markup).
  useIsomorphicLayoutEffect(() => {
    try {
      if (sessionStorage.getItem(WAVE_SEEN_KEY) === "1") setHasIntroPlayed(true)
    } catch {
      // sessionStorage can throw (private mode / blocked); just play the intro.
    }
  }, [])

  const handClassName = [
    "invite-hand-flourish-v2 hidden md:block",
    !hasIntroPlayed && !isReplaying ? "is-introing" : "",
    isReplaying ? "is-replaying" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="dark relative isolate flex min-h-svh items-center justify-center overflow-hidden bg-[#09070d] p-6 pb-28 text-white">
      {/* Attend eggplant over a dark base. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(120%_120%_at_50%_-15%,#5a3388_0%,#2d1647_34%,#09070d_78%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 z-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />

      <div
        className={["relative z-10 mx-auto w-full max-w-[400px]", wave ? "invite-accept-frame-v2" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {wave && (
          <>
            <div
              aria-hidden="true"
              className="invite-edge-hotspot-v2 hidden md:block"
              onMouseEnter={() => {
                if (hasIntroPlayed && !isReplaying) setIsReplaying(true)
              }}
            />
            <span
              aria-hidden="true"
              className={handClassName}
              onAnimationEnd={(event) => {
                if (event.animationName === "invite-hand-v2") {
                  setHasIntroPlayed(true)
                  try {
                    sessionStorage.setItem(WAVE_SEEN_KEY, "1")
                  } catch {
                    // Non-fatal — the intro just won't be remembered this session.
                  }
                }
                if (event.animationName === "invite-hand-hover-v2") setIsReplaying(false)
              }}
            >
              👋
            </span>
          </>
        )}

        <div className="invite-accept-card relative z-10 rounded-2xl border border-white/10 bg-surface p-8 shadow-2xl shadow-black/40 sm:p-10">
          <div className="mb-7 flex flex-col items-center text-center">
            <img
              src="https://files.attend.vip/logo/logo_extra-small.svg"
              alt="Attend"
              className="mb-4 h-3.5 w-auto brightness-0 invert"
            />
            <h1 className="text-[17px] font-semibold tracking-tightish text-white">{title}</h1>
            {subtitle && <p className="mt-1 text-[13px] text-white/55">{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>

      <footer className="absolute inset-x-0 bottom-0 z-10 px-6 pb-6 text-center text-[11px] text-white/45">
        © 2026 Kardoe Incorporated{" · "}
        <a
          href="https://www.attend.vip/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition hover:text-white"
        >
          Terms
        </a>
        {" · "}
        <a
          href="https://www.attend.vip/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition hover:text-white"
        >
          Privacy Policy
        </a>
      </footer>
    </div>
  )
}

// Controls styled for the dark auth card (Base UI Button + native/Base-UI Input).
export const fieldCls =
  "h-10 w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 text-[14px] text-white outline-none transition placeholder:text-white/30 focus:border-plum-400/60 focus:ring-2 focus:ring-plum-400/25"

export const labelCls = "mb-1.5 block text-[12px] font-semibold text-white/70"

export function PrimaryButton({
  children,
  disabled,
  type = "submit",
  onClick,
}: {
  children: ReactNode
  disabled?: boolean
  type?: "submit" | "button"
  onClick?: () => void
}) {
  return (
    <ButtonPrimitive
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="h-10 w-full rounded-xl text-[14px] font-semibold text-white shadow-fluent-sm transition active:scale-[.99] disabled:opacity-40"
      style={{ background: "linear-gradient(180deg, #7e4fb3, #552f7c)" }}
    >
      {children}
    </ButtonPrimitive>
  )
}

// Segmented 6-box OTP input. Auto-advances on entry, backspaces to the prior
// box, accepts a pasted code, and reports the joined digit string upward.
export function OtpInput({
  value,
  onChange,
  length = 6,
  autoFocus,
  onComplete,
}: {
  value: string
  onChange: (v: string) => void
  length?: number
  autoFocus?: boolean
  onComplete?: (v: string) => void
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? "")

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
  }

  const setAt = (i: number, d: string) => {
    const arr = value.split("")
    arr[i] = d
    commit(arr.join("").slice(0, length))
    if (d && i < length - 1) refs.current[i + 1]?.focus()
  }

  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus()
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus()
    } else if (e.key === "ArrowRight" && i < length - 1) {
      refs.current[i + 1]?.focus()
    }
  }

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length)
    if (!pasted) return
    commit(pasted)
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <div className="flex items-center justify-center gap-2" onPaste={onPaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          autoFocus={autoFocus && i === 0}
          value={d}
          onChange={(e) => setAt(i, e.target.value.replace(/\D/g, "").slice(-1))}
          onKeyDown={(e) => onKey(i, e)}
          onFocus={(e) => e.target.select()}
          className="h-12 w-11 rounded-xl border border-white/12 bg-white/[0.04] text-center font-mono text-[20px] font-semibold text-white outline-none transition focus:border-plum-400/60 focus:ring-2 focus:ring-plum-400/25"
        />
      ))}
    </div>
  )
}

export function TextLink({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="text-[12px] text-white/55 transition hover:text-white disabled:opacity-50"
    >
      {children}
    </button>
  )
}
